import type { AxiosInstance } from 'axios';

import type {
  CapabilityLocationMap,
  CapabilityNode,
  CapabilityReferenceItem,
  CapabilityReferencePage,
  CapabilityRefType,
  CreateCustomEquipmentInput,
  CreateCustomMaterialInput,
  CreateStorefrontCapabilityInput,
  StorefrontCapabilitySelection,
  StorefrontCapabilitySpec,
  UpsertCapabilitySpecInput,
} from '@accio3d/shared-ui/types/capability';
import { topLevelFromNodes } from '@accio3d/shared-ui/lib/capabilityTaxonomy';

export interface CapabilityTreeItem extends CapabilityNode {
  children: CapabilityTreeItem[];
}

interface CapabilityListResponse {
  data: CapabilityNode[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Capability taxonomy + storefront-selection API. Drill-down fetches children
 * per parent; the top level is bootstrapped from the tree endpoint (the flat
 * list has no "roots only" filter), starting at the branches under the single
 * root so suppliers begin at Manufacturing / Engineering.
 */
export function topLevelFromTree(tree: CapabilityTreeItem[]): CapabilityNode[] {
  const strip = ({ children: _children, ...node }: CapabilityTreeItem): CapabilityNode => node;
  // Roots and their direct children are all the rule ever inspects.
  return topLevelFromNodes([
    ...tree.map(strip),
    ...tree.flatMap((item) => item.children.map(strip)),
  ]);
}

export function createCapabilitiesService(client: AxiosInstance) {
  return {
    /** Children of a node; `null` returns the top-level service branches. */
    async listChildren(parentId: string | null): Promise<CapabilityNode[]> {
      if (parentId === null) {
        const { data } = await client.get<CapabilityTreeItem[]>('/capabilities/tree');
        return topLevelFromTree(data);
      }
      const { data } = await client.get<CapabilityListResponse>('/capabilities', {
        params: { parentId, limit: 100 },
      });
      return data.data;
    },

    /** Full nested active taxonomy (used to derive node → parent-category labels). */
    async getTree(): Promise<CapabilityTreeItem[]> {
      const { data } = await client.get<CapabilityTreeItem[]>('/capabilities/tree');
      return data;
    },

    async listReferences(
      nodeId: string,
      params: {
        search?: string;
        page: number;
        limit: number;
        as?: string;
        supplierId?: string;
        equipmentId?: string;
      },
    ): Promise<CapabilityReferencePage> {
      const { data } = await client.get<CapabilityReferencePage>(
        `/capabilities/${nodeId}/references`,
        { params },
      );
      return data;
    },

    async listSelections(supplierId: string): Promise<StorefrontCapabilitySelection[]> {
      const { data } = await client.get<StorefrontCapabilitySelection[]>(
        `/suppliers/${supplierId}/storefront/capability-selections`,
      );
      return data;
    },

    async addSelection(
      supplierId: string,
      body: CreateStorefrontCapabilityInput,
    ): Promise<StorefrontCapabilitySelection> {
      const { data } = await client.post<StorefrontCapabilitySelection>(
        `/suppliers/${supplierId}/storefront/capability-selections`,
        body,
      );
      return data;
    },

    async removeSelection(supplierId: string, selectionId: string): Promise<void> {
      await client.delete(
        `/suppliers/${supplierId}/storefront/capability-selections/${selectionId}`,
      );
    },

    async createCustomMaterial(
      supplierId: string,
      body: CreateCustomMaterialInput,
    ): Promise<CapabilityReferenceItem> {
      const { data } = await client.post<CapabilityReferenceItem>(
        `/suppliers/${supplierId}/storefront/materials`,
        body,
      );
      return data;
    },

    async createCustomEquipment(
      supplierId: string,
      body: CreateCustomEquipmentInput,
    ): Promise<CapabilityReferenceItem> {
      const { data } = await client.post<CapabilityReferenceItem>(
        `/suppliers/${supplierId}/storefront/equipment`,
        body,
      );
      return data;
    },

    /** The machine types a supplier can register a custom instance against. */
    async listMachineTypes(
      supplierId: string,
      as: CapabilityRefType,
    ): Promise<CapabilityReferenceItem[]> {
      const { data } = await client.get<CapabilityReferenceItem[]>(
        `/suppliers/${supplierId}/storefront/equipment/machine-types`,
        { params: { as } },
      );
      return data;
    },

    /** Every structured capability form the supplier has filled in. */
    async listCapabilitySpecs(supplierId: string): Promise<StorefrontCapabilitySpec[]> {
      const { data } = await client.get<StorefrontCapabilitySpec[]>(
        `/suppliers/${supplierId}/storefront/capability-specs`,
      );
      return data;
    },

    async upsertCapabilitySpec(
      supplierId: string,
      { capabilityNodeId, ...body }: UpsertCapabilitySpecInput,
    ): Promise<StorefrontCapabilitySpec> {
      const { data } = await client.put<StorefrontCapabilitySpec>(
        `/suppliers/${supplierId}/storefront/capability-specs/${capabilityNodeId}`,
        body,
      );
      return data;
    },

    /** Every capability→site link the supplier has, keyed by grain. */
    async getCapabilityLocations(supplierId: string): Promise<CapabilityLocationMap> {
      const { data } = await client.get<CapabilityLocationMap>(
        `/suppliers/${supplierId}/storefront/capability-locations`,
      );
      return data;
    },

    async setEquipmentLocations(
      supplierId: string,
      equipmentId: string,
      locationIds: string[],
    ): Promise<CapabilityLocationMap> {
      const { data } = await client.put<CapabilityLocationMap>(
        `/suppliers/${supplierId}/storefront/capability-locations/equipment/${equipmentId}`,
        { locationIds },
      );
      return data;
    },

    async setNodeLocations(
      supplierId: string,
      capabilityNodeId: string,
      locationIds: string[],
    ): Promise<CapabilityLocationMap> {
      const { data } = await client.put<CapabilityLocationMap>(
        `/suppliers/${supplierId}/storefront/capability-locations/node/${capabilityNodeId}`,
        { locationIds },
      );
      return data;
    },
  };
}

export type CapabilitiesService = ReturnType<typeof createCapabilitiesService>;
